import React, { useEffect, useState } from "react";
import {
  OPERATIONS_JOURNAL_PAGE_SIZE,
  formatAmount,
} from "../../../OperationsJournal/mock/operationsJournalMock";
import { getDataLayerLabel } from "../../../OperationsJournal/constants/dataLayers";
import useJournalFilters from "../../../OperationsJournal/hooks/useJournalFilters";
import useSourceJournal from "../../../OperationsJournal/hooks/useSourceJournal";
import useColumnOrder from "../../../OperationsJournal/hooks/useColumnOrder";
import JournalFilters from "../../../OperationsJournal/components/JournalFilters/JournalFilters";
import JournalTable from "../../../OperationsJournal/components/JournalTable/JournalTable";
import JournalPagination from "../../../OperationsJournal/components/JournalPagination/JournalPagination";
import OperationDetails from "../../../OperationsJournal/components/OperationDetails/OperationDetails";
import SelectionBar, { SelectionStat } from "../SelectionBar/SelectionBar";
import { ALLOCATION_DATA_LAYER } from "../../constants";

/**
 * Шаг 1: выбор операций журнала (только управленческие данные).
 * selected — Map id → строка, чтобы выбор сохранялся при смене фильтров и страниц.
 */
function OperationsStep({ selected, onSelectedChange, onContinue }) {
  const [viewMode, setViewMode] = useState("compact");
  const [unit, setUnit] = useState("tenge");
  const [currentPage, setCurrentPage] = useState(1);
  const [openedOperation, setOpenedOperation] = useState(null);
  const { filters, updateFilters, resetFilters, hasActiveFilters } =
    useJournalFilters();
  const { columnOrder, setColumnOrder, visibleColumns } = useColumnOrder();
  const journal = useSourceJournal({
    enabled: true,
    filters,
    page: currentPage,
    pageSize: OPERATIONS_JOURNAL_PAGE_SIZE,
    view: "management",
  });
  const rows = journal.rows;

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const totalPages = Math.max(1, Math.ceil((journal.total || 0) / OPERATIONS_JOURNAL_PAGE_SIZE));
  const pageStart = (currentPage - 1) * OPERATIONS_JOURNAL_PAGE_SIZE;
  const notSelectedInFilter = rows.filter((row) => !selected.has(row.id));

  const selectedIds = new Set(selected.keys());
  const selectedTotal = [...selected.values()].reduce(
    (sum, row) => sum + (row.amount || 0),
    0
  );

  const setRowsChecked = (targetRows, checked) => {
    const next = new Map(selected);
    targetRows.forEach((row) => {
      if (checked) next.set(row.id, row);
      else next.delete(row.id);
    });
    onSelectedChange(next);
  };

  const toggleRow = (row) => setRowsChecked([row], !selected.has(row.id));

  return (
    <div className="al-operations">
      <h3 className="al-section-title">Выберите операции для распределения</h3>

      <JournalFilters
        filters={filters}
        onChange={updateFilters}
        onReset={resetFilters}
        hasActiveFilters={hasActiveFilters}
        searchPlaceholder="Поиск по документу, комментарию..."
        hideTypeAndSource
        hideExtraFilters
        showAccountFilter
        dataView={ALLOCATION_DATA_LAYER}
        dataViewLocked
        dataViewLockedHint="В распределении участвуют только управленческие данные"
        unit={unit}
        onUnitChange={setUnit}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        columnOrder={columnOrder}
        onColumnOrderChange={setColumnOrder}
      />

      <div className="oj-table-card">
        <div className={`oj-table-banner oj-table-banner--${ALLOCATION_DATA_LAYER}`}>
          <span className="oj-table-banner__dot" />
          {getDataLayerLabel(ALLOCATION_DATA_LAYER)}
        </div>

        <JournalTable
          rows={rows}
          pageStart={pageStart}
          viewMode={viewMode}
          visibleColumns={visibleColumns}
          unit={unit}
          selectedId={openedOperation?.id}
          onRowClick={toggleRow}
          onOpenDocument={setOpenedOperation}
          selection={{
            selectedIds,
            onToggle: toggleRow,
            onTogglePage: setRowsChecked,
          }}
        />

        {journal.loading && rows.length === 0 && (
          <div className="oj-empty-state">Загрузка журнала...</div>
        )}
        {journal.error && <div className="oj-empty-state">{journal.error}</div>}
        {!journal.loading && !journal.error && rows.length === 0 && (
          <div className="oj-empty-state">
            По выбранным фильтрам операций не найдено
          </div>
        )}

        <JournalPagination
          page={currentPage}
          totalPages={totalPages}
          shown={rows.length}
          total={journal.total}
          onChange={setCurrentPage}
        />
      </div>

      <SelectionBar continueDisabled={selected.size === 0} onContinue={onContinue}>
        <SelectionStat label="Выбрано операций" value={selected.size} />
        <SelectionStat label="Общая сумма" value={formatAmount(selectedTotal, unit)} />
        {notSelectedInFilter.length > 0 && (
          <button
            type="button"
            className="al-link"
            onClick={() => setRowsChecked(notSelectedInFilter, true)}
          >
            Выбрать на странице ({notSelectedInFilter.length})
          </button>
        )}
        {selected.size > 0 && (
          <button
            type="button"
            className="al-link al-link--muted"
            onClick={() => onSelectedChange(new Map())}
          >
            Снять выбор
          </button>
        )}
      </SelectionBar>

      <OperationDetails
        operation={openedOperation}
        unit={unit}
        dataLayer={ALLOCATION_DATA_LAYER}
        readOnly
        onClose={() => setOpenedOperation(null)}
      />
    </div>
  );
}

export default OperationsStep;
